import React, { useEffect, useState } from 'react';

import { Stack, Card, CardContent, Divider, Box, ListItem, List, ListItemIcon } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { faBlind, faDeaf, faWheelchair } from '@fortawesome/free-solid-svg-icons';
import PhoneIcon from '@mui/icons-material/Phone';
import MailIcon from '@mui/icons-material/Mail';
import LanguageIcon from '@mui/icons-material/Language';
import WebAssetIcon from '@mui/icons-material/WebAsset';
import PinDropIcon from '@mui/icons-material/PinDrop';

import { FeatureTypography } from 'handimap-react-lib/components/Typography/FeatureTypography';
import { FeatureTextItemsList } from 'handimap-react-lib/components/Select/CustomListItem';
import { FontAwesomeSvgIcon } from 'handimap-react-lib/components/FontAwesomeSvgIcon';
import { config } from 'handimap-react-lib/config.js';
import SvgS3AIcon from 'handimap-react-lib/components/icons/mentIcon';


export const SelectedFeatureLineDetails = ({ apiFeature, layerID, getFeatureInfoAttrs, profilAttributeAccessMapping }) => {
  /**
     * component for general LineString type geometry
     */

  const style = {
    card: {
      display: 'flex',
      width: '90%',
      alignSelf: 'center'
    },
    card2: {
      display: 'flex',
      width: '95%',
      alignSelf: 'center',
    }
  };

  const {t} = useTranslation();

  const profil = useSelector(state => state.profils.profil);

  const [featureTitle, setFeatureTitle] = useState(null);
  const [featureDescription, setFeatureDescription] = useState(null);
  const [featureAdresse, setFeatureAdresse] = useState(null);
  const [featurePhone, setFeaturePhone] = useState(null);
  const [featureEmail, setFeatureEmail] = useState(null);
  const [featureWebsite, setFeatureWebsite] = useState(null);
  const [featureHeraultTourisme, setFeatureHeraultTourisme] = useState(null);
  const [featureDictAccess, setFeatureDictAccess] = useState({});
  const [logoTH, setLogoTH] = useState(false);
  const [logoAccDest, setLogoAccDest] = useState(false);

  const getColorAndLabelAccForAttr = (profil, icon, handicapLabel) => {
    const accKey = profilAttributeAccessMapping[profil]['acc-key'];
    const accDesc = profilAttributeAccessMapping[profil]['desc-th'];
    if (apiFeature.properties[accKey] === 0) {
      var label = '';
      if (apiFeature.properties[accDesc] !== null && apiFeature.properties[accDesc] !== '' && apiFeature.properties[accDesc] !== undefined) {
        label = apiFeature.properties[accDesc];
      } else {
        label = t('selectedFeature.acc');
      }
      return {
        color: '#6ba328',
        label: label,
        faIcon: icon
      };
    } else if (apiFeature.properties[accKey] === 1) {
      return {
        color: '#d4901e',
        label: `${t('selectedFeature.declared-acc')} ${handicapLabel}`,
        faIcon: icon
      };
    } else {
      return {
        color: '#666666',
        label: `${t('selectedFeature.not-spec')} ${handicapLabel}`,
        faIcon: icon
      };
    }
  }; 

  useEffect(() => {
    const featureTitleAttr = getFeatureInfoAttrs[layerID].title;
    const tempTitle = apiFeature.properties[featureTitleAttr];
    const title = tempTitle && tempTitle !== '' ? tempTitle: getFeatureInfoAttrs[layerID].defaultTitle;

    const featureDescriptionAttr = getFeatureInfoAttrs[layerID].description;
    const description = Object.keys(apiFeature.properties).includes(featureDescriptionAttr) ? apiFeature.properties[featureDescriptionAttr] : null;

    // init final result
    var finalResult = {};
    // Get acc for wheelchair and push it in the final result
    finalResult['wheelchair'] = getColorAndLabelAccForAttr('Déficience motrice', <FontAwesomeSvgIcon ariaLabel={t('selectedFeature.profile-wheelchair')} role='img' icon={faWheelchair} ariaHidden={false}/>, t('selectedFeature.wheelchair'));
    // Get acc for visually and push it in the final result
    finalResult['visually'] = getColorAndLabelAccForAttr('Déficience visuelle', <FontAwesomeSvgIcon ariaLabel={t('selectedFeature.profile-visual')} role='img' icon={faBlind} ariaHidden={false}/>, t('selectedFeature.visually'));
    // Get acc for hearing and push it in the final result
    finalResult['hearing'] = getColorAndLabelAccForAttr('Déficience auditive', <FontAwesomeSvgIcon ariaLabel={t('selectedFeature.profile-hearing')} role='img' icon={faDeaf} ariaHidden={false}/>, t('selectedFeature.hearing'));
    // Get acc for mentally and push it in the final result
    finalResult['mentally'] = getColorAndLabelAccForAttr('Déficience mentale', <SvgS3AIcon aria-label={t('selectedFeature.profile-mental')} aria-hidden={false}/>, t('selectedFeature.mentally'));
        
    setFeatureDictAccess(finalResult);
        
    if (apiFeature.properties[profilAttributeAccessMapping['Déficience motrice']['label-th']] === 1 || apiFeature.properties[profilAttributeAccessMapping['Déficience visuelle']['label-th']] === 1 || apiFeature.properties[profilAttributeAccessMapping['Déficience auditive']['label-th']] === 1 || apiFeature.properties[profilAttributeAccessMapping['Déficience mentale']['label-th']] === 1) {
      setLogoAccDest(true);
    } else {
      setLogoAccDest(false);
    }

    if ( profil in profilAttributeAccessMapping && apiFeature.properties[profilAttributeAccessMapping[profil]['label-th']] === 1) {
      setLogoTH(true);
    } else {
      setLogoTH(false);
    }

    setFeatureTitle(title);
    setFeatureDescription(description);
    if (apiFeature.properties.adresse1 || apiFeature.properties.codepostal || apiFeature.properties.commune) {
      setFeatureAdresse(`${apiFeature.properties.adresse1}, ${apiFeature.properties.codepostal} ${apiFeature.properties.commune}`);
    } else {
      setFeatureAdresse(null);
    }
    setFeaturePhone(apiFeature.properties.telfixe);
    setFeatureEmail(apiFeature.properties.email);
    setFeatureWebsite(apiFeature.properties.siteweb);
    setFeatureHeraultTourisme(apiFeature.properties.detailurl);
    
    return () => {
      setFeatureTitle(null);
      setFeatureDescription(null);
    };
  }, [apiFeature, t]);

  return (
    <Stack sx={{ width: '100%', alignItems: 'center' }} direction='column' spacing={2}>
      <Stack sx={{alignItems: 'center', textAlign: 'center'}} direction='column' spacing={2}>
        <FeatureTypography variant="h1" sx={{ fontSize: '1.2em', whiteSpace: 'normal'}}>
          {apiFeature.properties.activite ? `${apiFeature.properties.activite}: ` : ''} {featureTitle}
        </FeatureTypography>
        {logoTH &&
          <Box 
            component="img"
            sx={{
              marginX: '0.5em',
              backgroundColor: 'white',
              height: '8em'
            }} 
            src={config.url.PUBLIC_URL + '/nouveau-logo-TH.png'}
            alt={t('selectedFeature.aria-th-logo')}
          />
        }
      </Stack>

      <FeatureTypography component="p" sx={{ alignSelf: 'center', width: '95%', fontSize: '0.9em', color: 'white', whiteSpace: 'pre-line' }}>
        {featureDescription}
      </FeatureTypography>

      {(featureAdresse || featureHeraultTourisme || featurePhone || featureEmail || featureWebsite) &&
        <Card sx={style.card2}>
          <CardContent>
            {featureAdresse &&
              <Stack direction="row" spacing={1}>
                <PinDropIcon sx={{color: 'black', fontSize: '1.2em'}}/>
                <FeatureTypography component="p" sx={{ alignSelf: 'center', fontSize: '1em', color: 'black' }}>
                  {featureAdresse}
                </FeatureTypography>
              </Stack>
            }
            { featureHeraultTourisme && 
              <Stack direction="row" spacing={1}>
                <WebAssetIcon sx={{color: 'black', fontSize: '1.2em'}}/>
                <FeatureTypography component="p" sx={{color: 'black', fontSize: '1em'}}>
                  <a target="_blank" rel="noopener noreferrer" href={featureHeraultTourisme}> {t('selectedFeature.details-url')}</a>
                </FeatureTypography>
              </Stack>
            }
            { featurePhone && 
              <Stack direction="row" spacing={1}>
                <PhoneIcon sx={{color: 'black', fontSize: '1.2em'}}/>
                <FeatureTypography component="p" sx={{color: 'black', fontSize: '1em'}}>
                  <a target="_blank" rel="noopener noreferrer" href={`tel:${featurePhone}`}> {featurePhone}</a>
                </FeatureTypography>
              </Stack>
            }
            { featureEmail &&
            <Stack direction="row" spacing={1}>
              <MailIcon sx={{color: 'black', fontSize: '1.2em'}}/>
              <FeatureTypography component="p" sx={{color: 'black', fontSize: '1em'}}>
                <a target="_blank" rel="noopener noreferrer" href={`mailto:${featureEmail}`}> {featureEmail}</a>
              </FeatureTypography>
            </Stack>
            }
            { featureWebsite && 
              <Stack direction="row" spacing={1}>
                <LanguageIcon sx={{color: 'black', fontSize: '1.2em'}}/>
                <FeatureTypography component="p" sx={{color: 'black', fontSize: '1em'}}>
                  <a target="_blank" rel="noopener noreferrer" href={featureWebsite}> {t('selectedFeature.site-url')}</a>
                </FeatureTypography>
              </Stack>
            }
          </CardContent>
        </Card>
      }
      <Card sx={style.card}>
        <CardContent>
          <Box sx={{ display: 'flex', width: '100%' }}>
            <List dense={true}>
              {logoAccDest &&
                  <>
                    <ListItem key='logo-acc' style={{justifyContent:'center'}}>
                      <Box 
                        component="img"
                        sx={{
                          backgroundColor: 'white',
                          width: '85%'
                        }} 
                        src={config.url.PUBLIC_URL + '/Logo-DestinationAccessible.jpg'}
                        alt={t('selectedFeature.aria-dest-acc-logo')}
                      />
                    </ListItem>
                    <Divider aria-hidden={true} sx={{ marginBottom: '1em' }}/>
                  </>
              }
              {
                Object.keys(featureDictAccess).map((key) => {
                  return (
                    <ListItem key={key}>
                      <ListItemIcon sx={{
                        minWidth: '1em',
                        marginRight: '0.5em',
                        padding: '0.5em',
                        borderRadius : '0.5em',
                        border: '1.5px solid black',
                        display: 'flex',
                        justifyContent: 'center',
                        color: featureDictAccess[key].color}}
                      >
                        {featureDictAccess[key].faIcon}
                      </ListItemIcon>
                      <FeatureTextItemsList
                        primary={featureDictAccess[key].label}
                        disableTypography={true}
                        sx={{fontSize: '0.8em', whiteSpace: 'pre-line'}}
                      />
                    </ListItem>
                  );
                }
                )
              }
            </List>
          </Box>
        </CardContent>
      </Card>
    </Stack>
  );
};